import { createContext, ReactElement, ReactNode, useContext, useMemo, useState } from 'react'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { Organization } from '@modules-deprecated/app/organization/types/organization'

import { TableIds } from '../../../../../enums/tableIds'
import { InvoicesTableAccessor } from '../enums/invoicesTableAccessor'

interface ContextState {
  columns: InvoicesTableAccessor[]
  // Element in the top actions bar that receives the table's columns filtering. `null` until it is mounted.
  columnsFilteringContainer: HTMLElement | null
  newDocumentUrl: string
  options: {
    isFetchEnabled: boolean
    persistFiltersInUrl: boolean
    // Moves the columns filtering from the table's bottom bar to the top actions bar.
    showColumnsFilteringInActions: boolean
    showPeriodSelector: boolean
    showSearch: boolean
    showTitle: boolean
  }
  organization: Organization | undefined
  setColumnsFilteringContainer: (element: HTMLElement | null) => void
  tableId: TableIds
}

const InvoicesListContext = createContext<ContextState | undefined>(undefined)

export interface InvoicesListProviderProps {
  children?: ReactNode
  columns?: InvoicesTableAccessor[]
  newDocumentUrl: ContextState['newDocumentUrl']
  options: ContextState['options']
  tableId: ContextState['tableId']
}

const defaultColumns: InvoicesTableAccessor[] = [
  InvoicesTableAccessor.Status,
  InvoicesTableAccessor.InvoiceNumber,
  InvoicesTableAccessor.Type,
  InvoicesTableAccessor.ContactName,
  InvoicesTableAccessor.EInvoiceStatus,
  InvoicesTableAccessor.IssueDate,
  InvoicesTableAccessor.DueDate,
  InvoicesTableAccessor.PaidDate,
  InvoicesTableAccessor.Description,
  InvoicesTableAccessor.IntegrationStatus,
  InvoicesTableAccessor.NetTotal,
  InvoicesTableAccessor.InclVat,
]

export const InvoicesListProvider = ({
  children,
  columns,
  newDocumentUrl,
  options,
  tableId,
}: InvoicesListProviderProps): ReactElement => {
  const [columnsFilteringContainer, setColumnsFilteringContainer] = useState<HTMLElement | null>(null)
  const { organization } = useUserOrganization()
  const value = useMemo(
    () => ({
      columns: columns ?? defaultColumns,
      columnsFilteringContainer,
      newDocumentUrl,
      options,
      organization,
      setColumnsFilteringContainer,
      tableId,
    }),
    [columns, columnsFilteringContainer, newDocumentUrl, options, organization, tableId],
  )

  return <InvoicesListContext.Provider value={value}>{children}</InvoicesListContext.Provider>
}

export const useInvoicesList = () => {
  const context = useContext(InvoicesListContext)

  if (!context) {
    throw new Error('InvoicesListContext is missing in the module!')
  }

  return context
}
